// Slice 6 shapes: commitments, the set-up checklist and AI usage.

export type Domain = 'work' | 'personal';
export type Kind = 'task' | 'project' | 'recurring' | 'waiting' | 'delegated' | 'reference';
export type Status = 'active' | 'waiting' | 'deferred' | 'done' | 'dropped';
export type Tab = 'now' | 'waiting' | 'later' | 'done';

export interface Commitment {
  id: string; domain: Domain; kind: Kind; title: string; outcome: string; next_action: string; deadline: string | null;
  status: Status; waiting_on: string; follow_up_on: string | null; delegated_to: string; needs_planning: boolean; notes: string;
  source: 'manual' | 'manager' | 'import'; revision: number; created_at: string; updated_at: string; done_at: string | null;
}
export interface Counts { now: number; waiting: number; later: number; done: number }
export interface CommitmentList { items: Commitment[]; counts: Counts }

export const IMPORT_FIELDS = ['title', 'next_action', 'outcome', 'domain', 'kind', 'status', 'deadline', 'waiting_on', 'follow_up_on', 'delegated_to', 'notes'] as const;
export type ImportField = typeof IMPORT_FIELDS[number];
export const FIELD_LABEL: Record<ImportField, string> = {
  title: 'Task or project', next_action: 'Next action', outcome: 'Outcome', domain: 'Work or personal', kind: 'Type', status: 'Status',
  deadline: 'Deadline', waiting_on: 'Waiting on', follow_up_on: 'Follow-up date', delegated_to: 'Delegated to', notes: 'Notes',
};
export interface ImportRow { line: number; values: Partial<Commitment> & { title: string }; problems: string[]; duplicate: boolean; skip: boolean }
export interface ImportPreview {
  delimiter: 'tab' | 'comma'; headers: string[]; mapping: (ImportField | null)[]; rows: ImportRow[];
  summary: { total: number; ready: number; duplicates: number; invalid: number };
}

export const KIND_LABEL: Record<Kind, string> = { task: 'Task', project: 'Project', recurring: 'Recurring', waiting: 'Waiting for', delegated: 'Delegated', reference: 'Reference' };

export interface ChecklistStep { id: string; label: string; done: boolean; href: string }
export interface Checklist { show: boolean; dismissed: boolean; complete: boolean; testId: string | null; steps: ChecklistStep[] }

export interface Cost { calls: number; unknown: number; estimate: number | null; tokensIn: number | null; tokensOut: number | null }
export interface Usage {
  month: string; papers: number; calls: number; cost: number; unknown: number;
  jobs: { id: string; kind: string; status: string; error: string | null; provider: string; model: string; created_at: string; papers: number; students: number; test: string; cost: Cost }[];
}

/** Dollars for an estimate: small amounts keep their cents visible. */
export function money(n: number): string {
  if (n > 0 && n < 0.01) return 'under $0.01';
  return `$${n.toFixed(2)}`;
}

/** A job's cost in words. Unknown is said as unknown, never shown as $0. */
export function costText(c: Cost): string {
  if (c.calls === 0) return 'no AI calls yet';
  if (c.unknown > 0 || c.estimate === null) return 'unknown';
  return `about ${money(c.estimate)}`;
}

/** Whole days between today and an ISO date (negative = overdue). */
export function daysUntil(iso: string, now = new Date()): number {
  const [y, m, d] = iso.split('-').map(Number);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((new Date(y, m - 1, d).getTime() - today) / 86_400_000);
}

export function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', ...(y !== new Date().getFullYear() ? { year: 'numeric' } : {}) });
}

export function ageText(createdAt: string, now = Date.now()): string {
  const days = Math.floor((now - Date.parse(createdAt)) / 86_400_000);
  if (days <= 0) return 'added today';
  if (days === 1) return '1 day old';
  if (days < 60) return `${days} days old`;
  return `${Math.round(days / 30)} months old`;
}
